import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Amplify } from "aws-amplify"
import { Hub } from 'aws-amplify/utils'
import '@aws-amplify/auth/enable-oauth-listener'
import { useEffect, useState } from 'react'
import { envService } from './services/env-service'
import { BackofficePage } from './pages/backoffice-page'
import { UserMsg } from './cmps/util-cmps/user-msg'
import { LoginPage } from './pages/login-page'
import { ProtectedRoutes } from './cmps/login/protected-routes'
import { CompanyPage } from './pages/database/company-page'
import { DatabasePage } from './pages/database/database-page'
import { AuditControlPage } from './pages/database/audit-control-page'
import { EntitiesPage } from './pages/database/entities-page'
import { EntitySystemsPage } from './pages/database/entity-systems-page'
import { EmployeesPage } from './pages/database/employees-page'
import { ObjectivesPage } from './pages/database/objectives-page'
import { SystemsPage } from './pages/database/systems-page'
import { CompaniesPage } from './pages/database/companies-page'
import { ControlsPage } from './pages/database/controls-page'
import { HealthReportPage } from './pages/health-report-page'
import { AwsPage } from './pages/aws-page'
import { GlueTriggersPage } from './pages/aws/glue-triggers-page'
import { OnboardingPage } from './pages/onboarding/onboarding-page'
import { NavSideBar } from './cmps/nav-side-bar/nav-side-bar'
import ScytaleLogo from './assets/img/logo-scytale.png'
import { httpService } from './services/http-service'
import { useDispatch } from 'react-redux'
import { StoreDispatch } from './store/configure-store'
import { update } from './store/companies'


// Catch OAuth errors before Amplify.configure() processes the callback
Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signInWithRedirect_failure') {
    const error = String(payload.data?.error || '')
    const message = error.includes('PreSignUp')
      ? 'Your Google account is not authorized to access this application.'
      : 'Google sign-in failed. Please try again or use email and password.'
    sessionStorage.setItem('oauth_error', message)
  }
})

Amplify.configure({
  /* FIX - in Vue we didn't use auth variables on .env (only in env.test and so on) */
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID!,
      userPoolClientId: process.env.REACT_APP_USERPOOL_CLIENT_ID!,
      loginWith: {
        oauth: {
          domain: process.env.REACT_APP_USERPOOL_DOMAIN!,
          scopes: [
            "phone",
            "email",
            "profile",
            "openid",
            "aws.cognito.signin.user.admin",
          ],
          redirectSignIn: [`${window.location.origin}/`],
          redirectSignOut: [`${window.location.origin}/login`],
          responseType: "code",
        },
      },
      mfa: {
        status: 'on',
        totpEnabled: true
      }
    },
  }
});

function App() {
  const location = useLocation()
  const [isShowBars, setIsShowBars] = useState(false)
  const dispatch = useDispatch<StoreDispatch>()

  useEffect(() => {
    document.title = `Enterprise Backoffice - ${envService.label}`
  }, [])

  interface CompanyData {
    companies: any[];
  }
  const GET_COMPANIES = `
{
  companies{
    id
    name
  }
}
`

  const fetchCompaniesData = async () => {
    try {
      const data = await httpService.graphql('auditech', GET_COMPANIES)
      dispatch(update(data.companies))
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };


  useEffect(() => {
    if (['/login'].includes(location.pathname)) return setIsShowBars(false)
    setIsShowBars(true)
  }, [location])

  useEffect(() => {
    fetchCompaniesData()

  }, [])


  return (
    <section className={`app ${isShowBars ? "main-layout" : ""}`}>
      {isShowBars && <>
        <div className="auditech-logo" />
        <NavSideBar logo={ScytaleLogo} />
      </>}
      <main>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<ProtectedRoutes />}>
            <Route path="/" element={<Navigate to="/companies/audit-controls" replace />} />
            <Route path="*" element={<BackofficePage />} />
            <Route path="health-report" element={<HealthReportPage />} />
            <Route path="AWS" element={<AwsPage />}>
              <Route path='glue-triggers' element={<GlueTriggersPage />} />
            </Route>
            <Route path="onboarding" element={<OnboardingPage />} />

            {/* Company-scoped routes */}
            <Route path="companies" element={<CompanyPage />}>
              <Route path='audit-controls' element={<AuditControlPage />} />
              <Route path='entities' element={<EntitiesPage />} />
              <Route path='entity-context' element={<Navigate to="/companies/entities" replace />} />
              <Route path='entity-systems' element={<EntitySystemsPage />} />
              <Route path='employees' element={<EmployeesPage />} />
              <Route path='controls' element={<ControlsPage />} />
            </Route>

            {/* Global routes (database) */}
            <Route path="database" element={<DatabasePage />}>
              <Route path='objectives' element={<ObjectivesPage />} />
              <Route path='systems' element={<SystemsPage />} />
              <Route path='companies' element={<CompaniesPage />} />

              {/* Legacy redirects */}
              <Route path='audit-controls' element={<Navigate to="/companies/audit-controls" replace />} />
              <Route path='entities' element={<Navigate to="/companies/entities" replace />} />
              <Route path='entity-context' element={<Navigate to="/companies/entities" replace />} />
              <Route path='entity-systems' element={<Navigate to="/companies/entity-systems" replace />} />
              <Route path='employees' element={<Navigate to="/companies/employees" replace />} />
              <Route path='controls' element={<Navigate to="/companies/controls" replace />} />
              <Route path='setup' element={<Navigate to="/database/objectives" replace />} />
              <Route path='company' element={<Navigate to="/database/companies" replace />} />
              <Route path='organization' element={<Navigate to="/companies/entities" replace />} />
              <Route path='entity' element={<Navigate to="/companies/entities" replace />} />
              <Route path='entity-system' element={<Navigate to="/companies/entity-systems" replace />} />
              <Route path='employee' element={<Navigate to="/companies/employees" replace />} />
              <Route path='objective' element={<Navigate to="/database/objectives" replace />} />
              <Route path='system' element={<Navigate to="/database/systems" replace />} />
              <Route path='control' element={<Navigate to="/companies/controls" replace />} />
            </Route>

          </Route>
        </Routes>
      </main>

      <UserMsg />
    </section>
  );
}

export default App;
